import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { network } from '@utils'
import type { ReportAttachmentsByDocument } from './reports'

/**
 * Entry drill-downs are paginated in the database by a dedicated endpoint rather
 * than by re-running a whole report, which keeps accounts with thousands of
 * entries responsive.
 */
export type ReportEntryRow = {
  id: number
  number: string
  date: string
  description: string
  account_number: string
  account_name: string
  debet: number
  credit: number
  /** Signed by the account's normal balance side; null when document-scoped. */
  amount: number | null
  /** Running account balance seeded from the opening balance; null when document-scoped. */
  balance_csum: number | null
  document_id: number | null
}

export type ReportEntriesMeta = {
  account_number: string | null
  account_name: string | null
  document_number: string | null
  opening_balance: number | null
  /** Running balance before the oldest entry on this page; equals opening_balance on the last page. */
  page_opening_balance: number | null
  closing_balance: number | null
  debet_total: number
  credit_total: number
  date_from: string
  date_to: string | null
}

export type ReportEntriesResponse = {
  count: number
  next: number | null
  prev: number | null
  size: number
  /** Page actually returned; clamped to the last page when the request overshoots. */
  page: number
  page_count: number
  page_size: number
  results: ReportEntryRow[]
  meta: ReportEntriesMeta
  attachments_by_document: ReportAttachmentsByDocument
}

export type ReportEntriesParams = {
  account_number?: string
  document_number?: string
  date_from?: string
  date_to?: string
  tag_ids?: number[]
  page?: number
  page_size?: number
}

export const fetchReportEntries = async ({
  slug,
  params
}: {
  slug: string
  params: ReportEntriesParams
}): Promise<ReportEntriesResponse> => {
  const { data } = await network.httpClient.get<ReportEntriesResponse>(
    `/v1/business/${slug}/report/entries/`,
    { params }
  )
  return data
}

export const reportEntriesQueryKey = (slug: string, params: ReportEntriesParams) =>
  [slug, 'report-entries', params] as const

export const useReportEntries = ({
  slug,
  params,
  enabled = true
}: {
  slug: string
  params: ReportEntriesParams
  enabled?: boolean
}) => {
  return useQuery({
    queryKey: reportEntriesQueryKey(slug, params),
    queryFn: () => fetchReportEntries({ slug, params }),
    enabled: Boolean(slug) && enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData
  })
}
